import { useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Table,
  Paper,
  DialogActions,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  FormHelperText,
  FormControl,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomTypography from "../../components/common/form/CustomTypography";
import CustomTextField from "../../components/common/textFields/TextField";
import AppButton from "../../components/common/buttons/AppButton";
import CloseIcon from "@mui/icons-material/Close";
import { OrderProduct } from "../../../models/Order/OrderProduct";
import { useStores } from "../../../use-stores";
import { SortType } from "../../../models/SortType";
import AppTableHeaderCell from "../../components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../components/common/pages/AppTableRowCell";
import FormSmallTextField from "../../components/common/form/FormTextField";

interface AddPackConfigProps {
  list?: OrderProduct[];
  onSubmit: (packs: {
    label: string;
    quantity: number;
    products: Array<{
      product_id: number;
      quantity: number;
    }>;
  }) => void;
  onClose: () => void;
  isLoading?: boolean;
  handleInvoiceQuantity?: boolean;
}

export default function AddPackConfigPopup(props: AddPackConfigProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPacks, setSelectedPacks] = useState<OrderProduct[]>([]);
  const { productStore } = useStores();
  const [filteredPacks, setFilteredPacks] = useState<OrderProduct[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [packLabel, setPackLabel] = useState("");
  const [expectedQuantity, setExpectedQuantity] = useState<number | null>(null);
  const [expectedQuantityError, setExpectedQuantityError] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [packLabelError, setPackLabelError] = useState(false);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (props.list && props.list.length > 0) {
      setFilteredPacks(props.list);
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        handleDropdownClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectPack = (pack: OrderProduct) => {
    const index = selectedPacks.findIndex((p) => p.id === pack.id);
    if (index === -1) {
      const newPack = {
        ...pack,
        quantity: 1,
        avail_invoice_qty: 1,
      };
      setSelectedPacks([...selectedPacks, newPack]);
    } else {
      const updatedPacks = [...selectedPacks];
      updatedPacks.splice(index, 1);
      setSelectedPacks(updatedPacks);
    }
  };

  const handleSearch = (query: string) => {
    setSearchTerm(query);
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (!props.list && query.length > 2) {
      debounceTimeoutRef.current = setTimeout(() => {
        const params = {
          page_no: 1,
          sortBy: "id",
          sortType: SortType.ASC,
          name: query,
        };
        productStore
          .getProducts(params)
          .then(() => {
            setFilteredPacks(
              productStore.getOrderProductsFromProducts(productStore.products)
            );
          })
          .catch(() => {
            setFilteredPacks([]);
          });
        setIsDropdownOpen(true);
      }, 500);
    } else {
      if (props?.list?.length === 0) return;
      const filtered = props?.list?.filter(
        (pack: OrderProduct) =>
          pack.name!.toLowerCase().includes(query.toLowerCase()) ||
          pack.category_id.toString().includes(query)
      );
      setFilteredPacks(filtered || []);
      setIsDropdownOpen(true);
    }
  };

  const removePack = (pack: OrderProduct) => {
    const index = selectedPacks.findIndex((p) => p.id === pack.id);
    const updatedPacks = [...selectedPacks];
    updatedPacks.splice(index, 1);
    setSelectedPacks(updatedPacks);
  };

  const handleClearAll = () => {
    setSelectedPacks([]);
  };
  const updateConfirmedPacks = async () => {
    if (!packLabel.trim()) {
      setPackLabelError(true);
      return;
    } else {
      setPackLabelError(false);
    }

    if (expectedQuantity === null || expectedQuantity <= 0) {
      setExpectedQuantityError(true);
      return;
    } else {
      setExpectedQuantityError(false);
    }

    if (selectedPacks.length === 0) {
      return;
    }

    const updated_packs: OrderProduct[] = selectedPacks.map((pack) => ({
      ...pack,
      quantity: props.handleInvoiceQuantity
        ? pack.avail_invoice_qty || 1
        : pack.quantity || 1,
    }));

    props.onSubmit({
      label: packLabel,
      quantity: expectedQuantity,
      products: updated_packs.map((pack) => ({
        product_id: pack.id,
        quantity: props.handleInvoiceQuantity
          ? pack.avail_invoice_qty || 1
          : pack.quantity || 1,
      })),
    });
  };

  const updatePackQuantity = (value: number, pack: OrderProduct) => {
    const index = selectedPacks.findIndex(
      (selectedPack) => selectedPack.id === pack.id
    );
    if (index !== -1) {
      if (props.handleInvoiceQuantity) {
        const selectedPack = [...selectedPacks];
        selectedPack[index].avail_invoice_qty = value;
        setSelectedPacks(selectedPack);
      } else {
        const selectedPack = [...selectedPacks];
        selectedPack[index].quantity = value;
        setSelectedPacks(selectedPack);
      }
    }
  };

  const handleSearchBarClick = () => {
    if (filteredPacks.length > 0) {
      setIsDropdownOpen(true);
    }
  };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  return (
    <Dialog
      open
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "53.125rem",
          width: "100%",
          height: "100%",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingX: 5,
          paddingBottom: 2,
          paddingTop: 3,
        }}
      >
        <CustomTypography variant="heading4" fontWeight={700}>
          Pack Configuration
        </CustomTypography>
        <IconButton
          sx={{
            color: "black",
            "&:hover": {
              backgroundColor: "grey",
            },
          }}
          size="small"
          onClick={() => {
            setSelectedPacks([]);
            props.onClose();
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          paddingX: 5,
          paddingBottom: 1,
          paddingTop: 1,
        }}
      >
        <CustomTypography variant="heading6" fontWeight={700}>
          Pack Details
        </CustomTypography>
      </Box>
      <Box
        sx={{
          paddingX: 5,
          paddingBottom: 0,
          paddingTop: 1,
          display: "flex",
          gap: 2,
        }}
      >
        <FormControl sx={{ flex: 5 }} error={packLabelError}>
          <CustomTextField
            value={packLabel}
            onChange={(e) => {
              setPackLabel(e.target.value);
              if (e.target.value.trim()) {
                setPackLabelError(false);
              }
            }}
            width="100%"
            placeholder="Enter Pack Label"
            label="Pack Label"
            endAdornment={false}
          />
          {packLabelError && (
            <FormHelperText>Pack Label is required</FormHelperText>
          )}
        </FormControl>

        <FormControl sx={{ flex: 5 }} error={expectedQuantityError}>
          <CustomTextField
            value={expectedQuantity || ""}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              setExpectedQuantity(isNaN(value) ? null : value);
              if (!isNaN(value) && value > 0) {
                setExpectedQuantityError(false);
              }
            }}
            width="100%"
            placeholder="Enter Expected Quantity"
            label="Expected Quantity"
            type="number"
            endAdornment={false}
          />
          {expectedQuantityError && (
            <FormHelperText>Expected Quantity is required</FormHelperText>
          )}
        </FormControl>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingX: 5,
          paddingBottom: 1,
          paddingTop: 3,
        }}
      >
        <CustomTypography variant="heading6" fontWeight={700}>
          Add Products
        </CustomTypography>
      </Box>
      <Box sx={{ paddingX: 5, paddingBottom: 0, paddingTop: 1 }}>
        <CustomTextField
          value={searchTerm}
          onSearch={(query) => handleSearch(query)}
          width={"100%"}
          onClick={handleSearchBarClick}
        />
      </Box>
      <DialogContent sx={{ paddingX: 5, paddingTop: 0 }}>
        {isDropdownOpen && (
          <Box
            ref={dropdownRef}
            sx={{
              paddingX: 0,
              paddingTop: 0,
              paddingBottom: 0,
              position: "relative",
            }}
          >
            <Box
              sx={{
                maxHeight: 400,
                overflowY: "auto",
                boxShadow: 1,
                backgroundColor: "white",
                borderRadius: 2,
                marginTop: 0,
                padding: 2,
                position: "absolute",
                zIndex: 2,
                width: "100%",
              }}
            >
              {filteredPacks.map((pack: any) => (
                <Box
                  key={pack.id}
                  display="flex"
                  alignItems="center"
                  p={1}
                  borderRadius={1}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectPack(pack);
                    setSearchTerm("");
                  }}
                >
                  <FormControlLabel
                    sx={{
                      marginRight: 0,
                    }}
                    control={
                      <Checkbox
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleSelectPack(pack)}
                        checked={selectedPacks.some(
                          (selectedPack) => selectedPack.id === pack.id
                        )}
                        color="primary"
                      />
                    }
                    label={""}
                  />
                  <Box
                    sx={{
                      background: "rgba(242, 242, 242, 1)",
                      padding: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginRight: 2,
                      minHeight: "3.438rem",
                      maxHeight: "3.438rem",
                      borderRadius: "0.375rem",
                    }}
                  >
                    <img
                      style={{
                        maxHeight: "2.375rem",
                        minHeight: "2.375rem",
                        maxWidth: "2.375rem",
                        minWidth: "2.375rem",
                      }}
                      src={pack.image_url}
                      alt={pack.name}
                    />
                  </Box>
                  <Box sx={{ marginRight: 1.5 }}>
                    <Typography fontWeight="bold">{pack.name}</Typography>
                    <Box display="flex" gap={2}>
                      <Typography variant="body2" color="text.secondary">
                        Category ID: {pack.category_id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Price: {pack.selling_price}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
              {filteredPacks.length === 0 && (
                <Typography sx={{ padding: 2 }} color="text.secondary">
                  No packs found.
                </Typography>
              )}
            </Box>
          </Box>
        )}
        <Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            p={1}
          >
            <Box sx={{ width: "100%" }}>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 2, maxHeight: "40vh", minHeight: "40vh" }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        ID
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        PRODUCT
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        PRICE
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        GST%
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        QUANTITY
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="1.875rem" fontWeight={700}>
                        REMOVE
                      </AppTableHeaderCell>
                    </TableRow>
                  </TableHead>

                  {selectedPacks.length === 0 ? (
                    <TableBody>
                      <TableRow>
                        <TableCell
                          colSpan={12}
                          sx={{
                            borderBottom: "none",
                            padding: 3,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "fit-content",
                              textAlign: "center",
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              fontWeight={700}
                              color="rgba(218, 218, 218, 1)"
                            >
                              NO PACKS FOUND!
                            </CustomTypography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {selectedPacks?.map((pack: OrderProduct) => (
                        <TableRow key={pack.id}>
                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            {pack.id}
                          </AppTableRowCell>

                          <AppTableRowCell
                            minWidth="5rem"
                            sx={{
                              padding: 1,
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              <Box
                                sx={{
                                  background: "rgba(242, 242, 242, 1)",
                                  padding: 1,
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  marginRight: 2,
                                  minHeight: "3.438rem",
                                  maxHeight: "3.438rem",
                                  borderRadius: "0.375rem",
                                }}
                              >
                                <img
                                  style={{
                                    maxHeight: "2.375rem",
                                    minHeight: "2.375rem",
                                    maxWidth: "2.375rem",
                                    minWidth: "2.375rem",
                                  }}
                                  src={pack.image_url}
                                  alt={pack.name}
                                />
                              </Box>
                              <Box sx={{ textAlign: "left" }}>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={700}
                                  title={pack.name}
                                  sx={{
                                    cursor: "default",
                                  }}
                                >
                                  {pack.name}
                                </CustomTypography>

                                <CustomTypography
                                  variant="body3"
                                  fontWeight={500}
                                  color="rgba(111, 111, 111, 1)"
                                >
                                  Category ID: {pack.category_id}
                                </CustomTypography>
                              </Box>
                            </Box>
                          </AppTableRowCell>

                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            {pack.selling_price}
                          </AppTableRowCell>

                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            {pack.gst_percentage || "-"}
                          </AppTableRowCell>

                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            <FormSmallTextField
                              variant="outlined"
                              color="secondary"
                              type="number"
                              fontSize="0.875rem"
                              value={
                                props.handleInvoiceQuantity
                                  ? pack.avail_invoice_qty || ""
                                  : pack.quantity || ""
                              }
                              sx={{
                                maxWidth: "5rem",
                                mb: "0",
                                "& .MuiInputBase-input": {
                                  textAlign: "center",
                                },
                                "& input[type=number]": {
                                  textAlign: "center",
                                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                                    {
                                      display: "none",
                                    },
                                  MozAppearance: "textfield",
                                },
                              }}
                              onChange={(id, value) =>
                                updatePackQuantity(Number(value), pack)
                              }
                            />
                          </AppTableRowCell>

                          <AppTableRowCell minWidth="1.875rem" fontWeight={700}>
                            <IconButton
                              onClick={() => {
                                removePack(pack);
                              }}
                            >
                              <DeleteIcon
                                sx={{ color: "rgba(255, 0, 130, 0.8)" }}
                              />
                            </IconButton>
                          </AppTableRowCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
          {props.isLoading ? (
            <AppButton
              label={
                <>
                  <CircularProgress size={16} color="inherit" /> ...Loading
                </>
              }
              variant="primaryBlock"
              customStyles={{
                padding: 3,
                paddingTop: 1,
                paddingBottom: 1,
                margin: 0,
                fontWeight: 500,
                fontSize: "1rem",
                marginBottom: "1rem",
              }}
              onClick={function (): void {}}
            />
          ) : (
            <AppButton
              label="Confirm Packs"
              onClick={updateConfirmedPacks}
              disabled={selectedPacks.length === 0}
              variant="primaryBlock"
              customStyles={{
                padding: 3,
                paddingTop: 1,
                paddingBottom: 1,
                margin: 0,
                fontWeight: 500,
                fontSize: "1rem",
                marginBottom: "1rem",
              }}
            />
          )}

          <AppButton
            label="Clear All"
            onClick={handleClearAll}
            disabled={selectedPacks.length === 0}
            variant="grayscale"
            customStyles={{
              padding: 7.3,
              paddingTop: 1,
              paddingBottom: 1,
              margin: 0,
              fontWeight: 600,
              fontSize: "1rem",
              marginBottom: "1rem",
            }}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}
